/**
 * LINE Retention Popup SDK
 *
 * Chatboost-CV compatible popup SDK for LP exit-prevention.
 * Embedded on client LPs via:
 *
 *   <script>window.popupConfigGroupId = "123";</script>
 *   <script async src="https://static.example.com/js/popup-sdk.min.js"></script>
 *
 * Built as IIFE — no global namespace pollution.
 */

import type { PopupConfiguration, SDKContext, TriggerCleanup } from "./types";
import { activateAllTriggers } from "./triggers";
import { detectAndReportConversion } from "./conversion";
import { destroyLogger, initLogger } from "./logger";
import {
  getPageViewId,
  getSessionId,
  isInCooltime,
  isMobileDevice,
  isReferrerSameDomain,
  isWithinDisplayPeriod,
  saveUrlParamsToCookies,
} from "./utils";

// esbuild --define で注入されるビルド時定数
declare const __POPUP_API_BASE__: string;
declare const __LOG_API_BASE__: string;

// ---------------------------------------------------------------------------
// IIFE wrapper — entire SDK executes inside this closure
// ---------------------------------------------------------------------------

(function main() {
  try {
    init();
  } catch (err) {
    // SDK must NEVER crash the host page
    if (typeof console !== "undefined" && console.error) {
      console.error("[LR-SDK] Initialization failed:", err);
    }
  }
})();

// ---------------------------------------------------------------------------
// Initialization flow
// ---------------------------------------------------------------------------

async function init(): Promise<void> {
  // Step 0: Persist URL tracking parameters as cookies on every page load.
  // This runs before any popup logic so CV-related params are captured
  // even on pages where the popup does not activate.
  saveUrlParamsToCookies();

  // Step 0.5: Detect and report conversion if this is a CV (thank-you) page.
  // This runs independently of popup config — the thank-you page may not have
  // a popupConfigGroupId, but the CV tag (window.cbcvConvertInfo) still needs
  // to fire.
  const logApiBaseForCv =
    window.logApiBase || __LOG_API_BASE__ || window.location.origin;
  detectAndReportConversion(logApiBaseForCv);

  // Step 1: Read configuration globals
  const groupId = window.popupConfigGroupId;
  if (!groupId) {
    log("No popupConfigGroupId found. SDK will not activate.");
    return;
  }

  const popupApiBase =
    window.popupApiBase || __POPUP_API_BASE__ || window.location.origin;
  const logApiBase =
    window.logApiBase || __LOG_API_BASE__ || window.location.origin;

  // Step 2: Fetch popup configuration from API
  const config = await fetchPopupConfig(popupApiBase, groupId);
  if (!config) {
    log("No popup configuration returned. SDK will not activate.");
    return;
  }

  // Step 3: Validate display conditions
  if (!shouldActivate(config, groupId)) {
    return;
  }

  // Step 4: Build SDK context
  const isMobile = isMobileDevice();
  const ctx: SDKContext = {
    config,
    sessionId: getSessionId(),
    pageViewId: getPageViewId(),
    popupApiBase,
    logApiBase,
    isMobile,
  };

  // Step 5: Initialize logger (starts periodic flush + beacon handlers)
  initLogger(ctx);

  // Step 6: Activate triggers
  let cleanup: TriggerCleanup | null = activateAllTriggers(ctx);

  // Preload popup image for instant display
  preloadImage(config.media_url);

  log(
    `SDK activated. group=${groupId}, id=${config.id}, triggers: ` +
      `back=${config.should_show_by_browser_back}, ` +
      `time=${config.should_show_by_time} (${config.first_waiting_sec}s), ` +
      `reactivation=${config.should_show_by_reactivation}, ` +
      `inscroll=${config.should_show_by_inscroll} (${config.scroll_rate}%)`,
  );

  // Cleanup on page unload (belt-and-suspenders)
  window.addEventListener("pagehide", () => {
    if (cleanup) {
      cleanup();
      cleanup = null;
    }
    destroyLogger();
  });
}

// ---------------------------------------------------------------------------
// API fetch
// ---------------------------------------------------------------------------

async function fetchPopupConfig(
  apiBase: string,
  groupId: string,
): Promise<PopupConfiguration | null> {
  try {
    const url = `${apiBase}/api/popup-configurations?popup_config_group_id=${encodeURIComponent(groupId)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      // Credentials omitted — this is a public API with CORS
    });

    if (!response.ok) {
      log(`API returned ${response.status} for group ${groupId}`);
      return null;
    }

    const data: PopupConfiguration = await response.json();
    return data;
  } catch (err) {
    log("Failed to fetch popup configuration:", err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Activation guard
// ---------------------------------------------------------------------------

function shouldActivate(
  config: PopupConfiguration,
  groupId: string,
): boolean {
  // 1. Server says don't display
  if (!config.display) {
    log("config.display is false. Not activating.");
    return false;
  }

  // 2. Outside display time window
  if (
    !isWithinDisplayPeriod(
      config.show_time_lower_bound,
      config.show_time_upper_bound,
    )
  ) {
    log("Outside display time window. Not activating.");
    return false;
  }

  // 3. Device check: PC blocked if should_display_on_pc is false
  const isMobile = isMobileDevice();
  if (!isMobile && !config.should_display_on_pc) {
    log("PC device detected but should_display_on_pc is false. Not activating.");
    return false;
  }

  // 4. Cooltime (recently shown)
  if (isInCooltime(groupId)) {
    log("In cooltime period. Not activating.");
    return false;
  }

  // 5. Intra-domain suppression
  if (config.intra_domain_popup_suppression && isReferrerSameDomain()) {
    log("Same-domain referrer with intra_domain_popup_suppression. Not activating.");
    return false;
  }

  // 6. Force show override (debug)
  if (window.cbcvWillShowPopup === true) {
    log("Force show flag detected. Activating.");
    return true;
  }

  // 7. Sampling weight check (chatboost sends "1.0" as string)
  const weight = parseFloat(config.sampling_weight);
  if (!isNaN(weight) && weight < 1.0) {
    if (Math.random() > weight) {
      log(`Sampling weight ${weight} — randomly excluded. Not activating.`);
      return false;
    }
  }

  return true;
}

// ---------------------------------------------------------------------------
// Image preload
// ---------------------------------------------------------------------------

function preloadImage(url: string): void {
  if (!url) return;
  const img = new Image();
  img.src = url;
}

// ---------------------------------------------------------------------------
// Debug logging
// ---------------------------------------------------------------------------

function log(...args: unknown[]): void {
  if (typeof console !== "undefined" && console.log) {
    console.log("[LR-SDK]", ...args);
  }
}
